import { useEffect } from "react";
import { AxiosInstance } from "axios";
import { getSelf } from "src/api/auth";

interface UseChatBootstrapOptions {
  api: AxiosInstance;
  orgSlug: string;
  subOrgSlug: string;
  firstName: string | null | undefined;
  setFirstName: (name: string) => void;
  setLastName: (name: string) => void;
  setCurrentSuborgTitle: (title: string) => void;
}

// Three one-shot bootstrap effects bundled together: load the signed-in
// user's name into the org context, fetch the sub-org's permitted item title
// for the header, and stash any ?token= URL param into localStorage on first
// paint (then strip it from the URL).
export const useChatBootstrap = ({
  api,
  orgSlug,
  subOrgSlug,
  firstName,
  setFirstName,
  setLastName,
  setCurrentSuborgTitle,
}: UseChatBootstrapOptions) => {
  // Load user info if not already populated.
  useEffect(() => {
    if (firstName && firstName.length > 0) return;
    getSelf(api, orgSlug)
      .then((self) => {
        setFirstName(self.first_name);
        setLastName(self.last_name);
      })
      .catch((e) => {
        console.error("Error getting self:", e);
      });
  }, [api, orgSlug, firstName, setFirstName, setLastName]);

  // Fetch the sub-org's permitted-item title for the header label.
  useEffect(() => {
    if (subOrgSlug) {
      api.get(`/org/${orgSlug}/permitted/${subOrgSlug}`).then((data) => {
        if (data?.data?.today_item?.title) {
          setCurrentSuborgTitle(data.data.today_item.title);
        }
      });
    } else {
      setCurrentSuborgTitle("");
    }
  }, [api, orgSlug, subOrgSlug, setCurrentSuborgTitle]);

  // Stash an inbound ?token= and strip it from the URL.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    if (token) {
      localStorage.setItem("token", token);
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);
};
